/**
 * auto-captions-plausibility — "is this transcript believable for this audio?"
 *
 * The failure this exists for (prod bug report 2026-07-23, reproduced):
 * a 314 s video produced ONE word. Every stage reported success, export was
 * enabled, and the user burned + downloaded a 5-minute video with no visible
 * subtitles — twice, ~5.5 minutes each — before giving up and writing in.
 *
 * The pipeline's only quality gate was `if (!state.words.length)` — literally
 * "zero words". One word for five minutes sailed straight through as success.
 * Real speech runs ~2-3 words per second; that result was ~100x off, and we
 * already had every number needed to know it (word count, media duration, and
 * the VAD speech/total ratio) — we just never compared them.
 *
 * `assess({ words, durationSec, speechSec, hasVad })` returns a verdict:
 *   { level: 'ok' | 'sparse' | 'empty', wordsPerSpeechSec, speechRatio,
 *     reason, canRetryWithoutVad, languageSuspect }
 * reason 'speech_without_words' + languageSuspect: VAD kept at least half of
 * the media as speech and still few words came back — the spoken language is
 * the likely cause, not the speech search (chat 730, 29.10.2026).
 *
 * Deliberately conservative — a false "sparse" on a legitimately quiet clip is
 * only a dismissible note, but a missed one costs the user a 5-minute burn.
 * Short clips are never judged by rate (a 6 s clip with 2 words is fine), and
 * music/ambience videos with genuinely little speech surface as an honest
 * explanation rather than a silent empty result.
 *
 * Exposed as `window.__acPlausibility` (browser) + module.exports (node tests).
 */
(function () {
    'use strict';

    var DEFAULTS = {
        minMediaSec: 25,       // below this, too short to judge by rate
        // The USER-FACING truth: words per second of THEIR video. 0.06 ≈ under
        // 4 words a minute — at that point the burned video reads as "no
        // subtitles", which is exactly the complaint. Measured degenerate runs
        // sat at 0.003-0.027; a healthy interview is ~2.0.
        sparseWordsPerMediaSec: 0.06,
        // Secondary signal: rate against the audio Whisper actually saw.
        sparseWordsPerSpeechSec: 0.55,  // real speech ≈ 2-3 w/s
        lowSpeechRatio: 0.12,  // VAD kept <12% of the media as "speech"
        // VAD kept at least this share of the media as speech → a whole-file
        // re-run cannot be the fix (chat 730, 29.10.2026: 4188 s of speech in a
        // 5545 s video, 221 words — the Russian-only model was fed Chinese; the
        // offered «Transcribe the whole file» bought another 45 minutes of the
        // same). Speech without words there points at the LANGUAGE.
        plentySpeechRatio: 0.5,
    };

    function num(v, d) { return (typeof v === 'number' && isFinite(v) && v >= 0) ? v : d; }

    function assess(input, opts) {
        input = input || {}; opts = opts || {};
        var O = {};
        for (var k in DEFAULTS) O[k] = (opts[k] != null ? opts[k] : DEFAULTS[k]);

        var words = num(input.words, 0);
        var durationSec = num(input.durationSec, 0);
        var hasVad = !!input.hasVad;
        var speechSec = hasVad ? num(input.speechSec, 0) : durationSec;

        var speechRatio = durationSec > 0 ? (speechSec / durationSec) : null;
        // Two rates, and they answer different questions:
        //  - perMedia  = what the USER perceives ("my 5-min video has no subs")
        //  - perSpeech = how Whisper did on the audio it was actually handed
        var wordsPerMediaSec = words / Math.max(1, durationSec);
        var wordsPerSpeechSec = words / Math.max(1, hasVad ? speechSec : durationSec);

        var out = {
            level: 'ok',
            words: words,
            durationSec: durationSec,
            speechSec: hasVad ? speechSec : null,
            speechRatio: speechRatio,
            wordsPerMediaSec: wordsPerMediaSec,
            wordsPerSpeechSec: wordsPerSpeechSec,
            reason: null,
            canRetryWithoutVad: false,
            languageSuspect: false,
        };
        var plentySpeech = hasVad && speechRatio != null && speechRatio >= O.plentySpeechRatio;

        if (words === 0) {
            out.level = 'empty';
            out.reason = 'no_words';
            out.canRetryWithoutVad = hasVad && durationSec >= O.minMediaSec && !plentySpeech;
            out.languageSuspect = plentySpeech && durationSec >= O.minMediaSec;
            return out;
        }

        // Too short to judge by rate — a brief clip legitimately has few words.
        if (durationSec < O.minMediaSec) return out;

        // Primary gate: is there enough text to be worth burning at all?
        var tooFewForVideo = wordsPerMediaSec < O.sparseWordsPerMediaSec;
        // Secondary: VAD threw away nearly all the audio (noisy room, distant
        // mic) AND what came back is thin — Whisper only ever saw seconds.
        var vadAteAudio = hasVad && speechRatio != null && speechRatio < O.lowSpeechRatio
            && wordsPerSpeechSec < O.sparseWordsPerSpeechSec;

        if (tooFewForVideo || vadAteAudio) {
            out.level = 'sparse';
            out.reason = (hasVad && speechRatio != null && speechRatio < O.lowSpeechRatio)
                ? 'vad_kept_little_audio'
                : plentySpeech ? 'speech_without_words' : 'few_words_for_duration';
            out.canRetryWithoutVad = hasVad && !plentySpeech;
            out.languageSuspect = plentySpeech;
        }
        return out;
    }

    var api = { assess: assess, DEFAULTS: DEFAULTS };
    if (typeof window !== 'undefined') window.__acPlausibility = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
